import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentVersion } from '../types/AgentVersion';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Loader2, History, Copy, Globe, Webhook } from 'lucide-react';
import { cn } from '@/infrastructure/lib/utils';
import { toast } from 'sonner';
import { PageLoadingSpinner } from '@/shared/components/ui/page-loading-spinner';
import { AgentVersionService } from '../services/agentVersionService';
import { AgentTriggerService } from '../services/agentTriggerService';

interface TriggerConfig {
  path?: string;
  publicUrl?: string;
  fullUrl?: string;
  url?: string;
  [key: string]: unknown;
}

interface Trigger {
  trigger_type: string;
  trigger_config?: TriggerConfig;
  [key: string]: unknown;
}

interface AgentVersionsProps {
  agentId: string;
  onVersionRestored?: () => void;
}

export function AgentVersions({ agentId, onVersionRestored }: AgentVersionsProps) {
  const { t, i18n } = useTranslation();
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRestoring, setIsRestoring] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AgentVersion | null>(null);
  const [triggers, setTriggers] = useState<Record<string, Trigger[]>>({});

  const fetchVersions = async () => {
    try {
      // Usar o novo endpoint otimizado que já inclui informações de webhook
      const { data, error } = await AgentVersionService.getVersionsWithWebhooks(agentId);

      if (error) throw error;
      
      // Ordenar as versões por data de criação em ordem decrescente (mais recentes primeiro)
      const sortedVersions = [...(data || [])].sort((a, b) => {
        // Se created_at não estiver disponível, use updated_at ou qualquer outro campo de data
        const dateA = new Date(a.created_at || a.updated_at || 0);
        const dateB = new Date(b.created_at || b.updated_at || 0);
        return dateB.getTime() - dateA.getTime(); // Ordem decrescente
      });
      
      setVersions(sortedVersions);

      // O endpoint with-webhooks retorna webhook_urls (array de objetos com frontend_url, backend_url, path)
      const triggersData: Record<string, Trigger[]> = {};
      for (const version of data || []) {
        const webhookUrls = version.webhook_urls;
        if (webhookUrls && webhookUrls.length > 0) {
          triggersData[version.id] = webhookUrls.map(wh => ({
            trigger_type: 'webhook',
            trigger_config: {
              path: wh.path,
              publicUrl: wh.frontend_url,
              url: wh.backend_url
            }
          }));
        } else {
          triggersData[version.id] = [];
        }
      }
      
      setTriggers(triggersData);
    } catch (error) {
      // Fallback para o método antigo se o novo endpoint não estiver disponível
      try {
        const { data: fallbackData, error: fallbackError } = await AgentVersionService.getByAgentBaseId(agentId);
        if (fallbackError) throw fallbackError;
        
        const sortedVersions = [...(fallbackData || [])].sort((a, b) => {
          const dateA = new Date(a.created_at || a.updated_at || 0);
          const dateB = new Date(b.created_at || b.updated_at || 0);
          return dateB.getTime() - dateA.getTime();
        });
        
        setVersions(sortedVersions);
        
        // Buscar triggers da forma antiga
        const triggersData: Record<string, Trigger[]> = {};
        for (const version of fallbackData || []) {
          const { data: versionTriggers, error: triggersError } = await AgentTriggerService.getByVersionId(version.id);
          if (triggersError) throw triggersError;
          triggersData[version.id] = versionTriggers || [];
        }
        
        setTriggers(triggersData);
      } catch (fallbackError) {
        // Se ambos os métodos falharam, definir estado vazio para evitar crash
        setVersions([]);
        setTriggers({});
        toast.error(t('agents.settings.versions.loadError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const handleRestore = async () => {
    if (!selectedVersion) return;

    setIsRestoring(true);
    try {
      // Usar o novo endpoint de restauração que cria uma nova versão baseada na selecionada
      const { error } = await AgentVersionService.restoreVersion(selectedVersion.id);

      if (error) {
        throw error;
      }

      toast.success(t('agents.settings.versions.restore.success'));
      setShowRestoreConfirm(false);
      setSelectedVersion(null);
      
      if (onVersionRestored) {
        onVersionRestored();
      }
      
      // Recarregar lista de versões por último
      fetchVersions();
    } catch (error) {
      toast.error(t('agents.settings.versions.restore.error'));
    } finally {
      setIsRestoring(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'current':
        return 'bg-green-500/15 text-green-700 border-green-600/20';
      case 'error':
        return 'bg-red-500/15 text-red-700 border-red-600/20';
      case 'archived':
        return 'bg-gray-500/15 text-gray-700 border-gray-600/20';
      default:
        return 'bg-yellow-500/15 text-yellow-700 border-yellow-600/20';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[400px]">
        <PageLoadingSpinner />
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
        <p className="text-lg font-medium">{t('agents.settings.versions.noVersions')}</p>
        <p className="text-sm mt-2">{t('agents.settings.versions.subtitle')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight">{t('agents.settings.versions.title')}</h2>
        <p className="text-sm text-muted-foreground mt-1">{t('agents.settings.versions.subtitle')}</p>
      </div>

      <ScrollArea className="h-[calc(100vh-15rem)]">
        <div className="space-y-4">
          {versions.map((version) => (
            <Card key={version.id} className="transition-all hover:shadow-md">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-3">
                      {version.version}
                      <Badge variant="outline" className={cn("ml-2", getStatusColor(version.status))}>
                        {t(`agents.settings.versions.statusLabels.${version.status || 'inactive'}`)}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {version.created_at && (
                        t('agents.settings.versions.createdAt', {
                          time: formatDistanceToNow(new Date(version.created_at), {
                            addSuffix: true,
                            locale: i18n.language === 'pt' ? ptBR : undefined
                          })
                        })
                      )}
                    </CardDescription>
                  </div>
                  {version.status !== 'current' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                      onClick={() => {
                        setSelectedVersion(version);
                        setShowRestoreConfirm(true);
                      }}
                    >
                      <History className="h-4 w-4 mr-2" />
                      {t('agents.settings.versions.restore.button')}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">
                      {version.description || t('agents.settings.general.noDescription')}
                    </p>
                  </div>

                  {triggers[version.id]?.length > 0 && (
                    <div className="border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-3">
                        <Webhook className="h-4 w-4" />
                        {t('agents.settings.versions.triggers')}
                      </div>
                      <div className="space-y-2">
                        {triggers[version.id].map((trigger: Trigger, index: number) => (
                          <div key={index} className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className="text-xs bg-purple-500/10 text-purple-700 border-purple-200"
                            >
                              {trigger.trigger_type}
                            </Badge>
                            {(trigger.trigger_config?.publicUrl || trigger.trigger_config?.fullUrl || trigger.trigger_config?.url) && (
                              <div className="flex-1 flex items-center gap-2 bg-background rounded border px-3 py-1.5">
                                <Globe className="h-4 w-4 text-muted-foreground" />
                                <code className="text-sm flex-1">
                                  {trigger.trigger_config.publicUrl || trigger.trigger_config.fullUrl || trigger.trigger_config.url}
                                </code>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 hover:bg-muted"
                                  onClick={() => {
                                    const triggerUrl = trigger.trigger_config?.publicUrl || trigger.trigger_config?.fullUrl || trigger.trigger_config?.url;
                                    navigator.clipboard.writeText(triggerUrl ?? '');
                                    toast.success(t('agents.settings.versions.pathCopied'));
                                  }}
                                >
                                  <Copy className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </ScrollArea>

      {/* Restore Confirmation Dialog */}
      <Dialog open={showRestoreConfirm} onOpenChange={setShowRestoreConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('agents.settings.versions.restore.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('agents.settings.versions.restore.confirmDescription')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRestoreConfirm(false);
                setSelectedVersion(null);
              }}
            >
              {t('agents.settings.versions.restore.cancel')}
            </Button>
            <Button
              onClick={handleRestore}
              disabled={isRestoring}
            >
              {isRestoring ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('agents.settings.versions.restore.confirm')}
                </>
              ) : (
                t('agents.settings.versions.restore.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
